import { Dispatch, FC, SetStateAction, useMemo } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import dayjs from 'dayjs'
import theme from 'theme'

/* Utils */
import { IntegrationInstance, IntegrationStatusMessage, ManifestIntegration } from 'models'
import { INSTANCE_EDIT_DISABLED_HINT, getInstanceEditUrl, isInstanceEditDisabled } from './utils'

/* Components */
import {
  Drawer,
  InstancesWrapper,
  IntegrationInstanceItem,
  ActionsWrapper,
  MetaDataWrapper,
  MetaDataItem,
  ErrorWrapper
} from './addInstanceDrawer.styles'
import { CategoryChip } from './components/integrationCard.styles'
import ThemeButton from '../../../components/buttons/primary/ThemeButton'
import { PermissionTooltip } from '../../../components/tooltip'

const EDIT_BUTTON_SX = {
  '&:hover': {
    svg: {
      path: {
        fill: 'none !important',
        stroke: '#292128 !important'
      }
    }
  }
}

const editButtonContent = (
  <>
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
      <path
        d="M11.9918 3.37641L13.0303 2.33795C13.4809 1.88735 14.2114 1.88735 14.662 2.33795C15.1127 2.78856 15.1127 3.51913 14.662 3.96974L5.81981 12.812C5.49447 13.1373 5.09319 13.3765 4.65224 13.5078L3 14L3.49217 12.3478C3.62352 11.9068 3.86267 11.5055 4.18801 11.1802L11.9918 3.37641ZM11.9918 3.37641L13.6154 5"
        stroke="#292128"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    Edit
  </>
)

interface Props {
  drawerOpen: boolean
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  integration: ManifestIntegration | null
  instance: IntegrationInstance | null
  renderAffectedCpis: () => JSX.Element | null
  instances: IntegrationInstance[]
  integrationType: 'add' | 'edit'
  handleDeleteInstance: () => void
  noEditPermission: boolean
  handleViewInstances: (integration: ManifestIntegration | null) => void
  handleAddInstance: (integrationName: string) => void
  integrations: ManifestIntegration[]
}

const IntegrationInstanceDrawer: FC<Props> = ({
  drawerOpen,
  setDrawerOpen,
  integration,
  instance,
  renderAffectedCpis,
  instances,
  integrations,
  integrationType,
  handleDeleteInstance,
  noEditPermission,
  handleViewInstances,
  handleAddInstance
}) => {
  const navigate = useNavigate()

  /* Read Only outranks the temporary block — it is the permanent reason. */
  const editBlockedReason = noEditPermission
    ? 'Read Only'
    : instance && isInstanceEditDisabled(instance)
    ? INSTANCE_EDIT_DISABLED_HINT
    : ''

  const hasInstances = useMemo(() => !!integration && integration.instances > 0, [integration])
  const integrationInstances = useMemo(
    () => (integration ? instances.filter((n) => n.integration_name === integration.name) : []),
    [integration, instances]
  )

  const renderInstanceCategories = () => {
    if (!instance || !integrations.length) return null

    const foundIntegration = integrations.find((c) => c.name === instance.integration_name)

    if (!foundIntegration) return null

    const categories = foundIntegration.categories

    return Array.isArray(categories) ? (
      categories.map((category: any) => <CategoryChip key={category} label={category} size="small" />)
    ) : (
      <CategoryChip label={categories} size="small" />
    )
  }

  if (!integration && !instance) return null
  return (
    <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} integrationtype={integrationType}>
      <Box className="wrapper-inner" role="presentation">
        <>
          <Box className="inner-scrollable-section">
            <Box className="header-container">
              <Box className="header">
                {instance ? (
                  <Typography className="title">
                    {instance.is_v2 ? instance.name || '' : (instance as any).name}
                  </Typography>
                ) : (
                  <Typography className="title">{integration?.display_name || ''}</Typography>
                )}

                <IconButton className="close-icon" onClick={() => setDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {instance && (instance as any).vendor && (instance as any).vendor.display_name && (
                <Box sx={{ height: '8px' }} />
              )}
              {integration && integration.vendor && (
                <Typography className="sub-header-title">{integration.vendor.display_name}</Typography>
              )}
              {instance && (instance as any).vendor && (
                <Typography className="sub-header-title">{(instance as any).vendor.display_name || ''}</Typography>
              )}
            </Box>

            {integration ? (
              <Box className="categories-wrapper">
                {Array.isArray(integration.categories) ? (
                  integration.categories.map((category: any) => (
                    <CategoryChip key={category} label={category} size="small" />
                  ))
                ) : (
                  <CategoryChip label={integration.categories} size="small" />
                )}
              </Box>
            ) : (
              <Box className="categories-wrapper">{renderInstanceCategories()}</Box>
            )}

            {integration && (
              <Box className="description-wrapper">
                <Typography className="description-title">About Integration</Typography>
                <Typography className="description-text">{integration.description}</Typography>
              </Box>
            )}

            {instance && (
              <Box className="description-wrapper">
                <Typography className="description-title">About Instance</Typography>
                <Typography className="description-text">{instance.description}</Typography>
              </Box>
            )}

            {instance && instance.syncStatus && instance.syncStatus.status === IntegrationStatusMessage.failed && (
              <ErrorWrapper>
                <Box className="header">
                  <Box className="icon-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M9 7.47703V10.5209M1.25349 13.261C0.532917 14.4785 1.43434 16 2.87629 16H15.1237C16.5657 16 17.4671 14.4785 16.7465 13.261L10.6228 2.91368C9.90182 1.69544 8.09818 1.69544 7.3772 2.91368L1.25349 13.261ZM9 12.9561H9.00625V12.9622H9V12.9561Z"
                        stroke="#E74C3C"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                  <Typography className="header-title">
                    The data synchronization for the data source has encountered a failure. Potential root causes may
                    include:
                  </Typography>
                </Box>
                <Box className="content">
                  <ul>
                    <li>Expired API key</li>
                    <li>Insufficient permissions associated with the API key for data collection</li>
                    <li>Inaccessibility of the data source</li>
                  </ul>
                </Box>
                <Box className="footer">
                  <Typography className="footer-text">
                    Please review the integration and API key configurations to resolve the issue
                  </Typography>
                </Box>
              </ErrorWrapper>
            )}

            {integrationType === 'edit' && instance && (
              <MetaDataWrapper>
                <Box className="metadata-inner">
                  <Typography className="metadata-title">Meta Data</Typography>

                  <MetaDataItem className="metadata-instance-item">
                    <Box className="metadata-blocks-list">
                      <Box className="metadata-block-item">
                        <Box className="metadata-block-icon-wrapper">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M5.08278 2.5V4.03947M12.3146 2.5V4.03947M2.5 13.2763V5.57895C2.5 4.72872 3.19381 4.03947 4.04967 4.03947H13.3477C14.2035 4.03947 14.8974 4.72872 14.8974 5.57895V8.14474M2.5 13.2763C2.5 14.1265 3.19381 14.8158 4.04967 14.8158H9.38742M2.5 13.2763V8.14474C2.5 7.29451 3.19381 6.60526 4.04967 6.60526H13.3477C14.2035 6.60526 14.8974 7.29451 14.8974 8.14474M14.8974 8.14474V9.34211M10.851 13.1908H13.1755M13.1755 13.1908H15.5M13.1755 13.1908V10.8816M13.1755 13.1908V15.5"
                              stroke="#A88CA4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Box>
                        <Box className="metadata-block-content-wrapper">
                          <Typography className="metadata-block-content-title">Date created</Typography>
                          <Typography className="metadata-block-content-text">
                            {dayjs(instance.created_at).format('MM/DD/YYYY')}
                          </Typography>
                        </Box>
                      </Box>

                      <Box className="metadata-block-item">
                        <Box className="metadata-block-icon-wrapper">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M13.5 5.625V7.875M13.5 7.875V10.125M13.5 7.875H15.75M13.5 7.875H11.25M9.5625 4.78125C9.5625 6.17922 8.42922 7.3125 7.03125 7.3125C5.63328 7.3125 4.5 6.17922 4.5 4.78125C4.5 3.38328 5.63328 2.25 7.03125 2.25C8.42922 2.25 9.5625 3.38328 9.5625 4.78125ZM2.25069 14.4257C2.25023 14.3985 2.25 14.3711 2.25 14.3438C2.25 11.7031 4.39064 9.5625 7.03125 9.5625C9.67186 9.5625 11.8125 11.7031 11.8125 14.3438V14.3457C11.8125 14.3724 11.8123 14.3991 11.8118 14.4257C10.4155 15.2664 8.77986 15.75 7.03125 15.75C5.28264 15.75 3.64698 15.2664 2.25069 14.4257Z"
                              stroke="#A88CA4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Box>
                        <Box className="metadata-block-content-wrapper">
                          <Typography className="metadata-block-content-title">Created by</Typography>
                          <Typography className="metadata-block-content-text">{instance.created_by}</Typography>
                        </Box>
                      </Box>

                      <Box className="metadata-block-item">
                        <Box className="metadata-block-icon-wrapper">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                          >
                            <path
                              d="M3.06579 0.5V2.03947M10.25 0.5V2.03947M0.5 11.2763V3.57895C0.5 2.72872 1.18925 2.03947 2.03947 2.03947H11.2763C12.1265 2.03947 12.8158 2.72872 12.8158 3.57895V6.14474M0.5 11.2763C0.5 12.1265 1.18925 12.8158 2.03947 12.8158H7.3421M0.5 11.2763V6.14474C0.5 5.29451 1.18925 4.60526 2.03947 4.60526H11.2763C12.1265 4.60526 12.8158 5.29451 12.8158 6.14474M12.8158 6.14474V7.3421"
                              stroke="#A88CA4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M13.3246 8.88629C13.0908 8.65249 12.7118 8.65249 12.478 8.88629L12.2141 9.1502L13.0607 9.99687L13.3246 9.73295C13.5584 9.49915 13.5584 9.12009 13.3246 8.88629Z"
                              fill="#A88CA4"
                            />
                            <path
                              d="M12.8188 10.2388L11.9721 9.39211L9.20091 12.1633C9.06024 12.304 8.95683 12.4775 8.90004 12.6682L8.71763 13.2805C8.6997 13.3407 8.7162 13.4059 8.76061 13.4503C8.80503 13.4947 8.87021 13.5112 8.9304 13.4933L9.54274 13.3109C9.7334 13.2541 9.90691 13.1507 10.0476 13.01L12.8188 10.2388Z"
                              fill="#A88CA4"
                            />
                          </svg>
                        </Box>
                        <Box className="metadata-block-content-wrapper">
                          <Typography className="metadata-block-content-title">Date edited</Typography>
                          <Typography className="metadata-block-content-text">
                            {dayjs(instance.updated_at).format('MM/DD/YYYY')}
                          </Typography>
                        </Box>
                      </Box>

                      <Box className="metadata-block-item">
                        <Box className="metadata-block-icon-wrapper">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M9.5625 4.78125C9.5625 6.17922 8.42922 7.3125 7.03125 7.3125C5.63328 7.3125 4.5 6.17922 4.5 4.78125C4.5 3.38328 5.63328 2.25 7.03125 2.25C8.42922 2.25 9.5625 3.38328 9.5625 4.78125Z"
                              stroke="#A88CA4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M2.25069 14.4257C2.25023 14.3985 2.25 14.3711 2.25 14.3438C2.25 11.7031 4.39064 9.5625 7.03125 9.5625C9.67186 9.5625 11.8125 11.7031 11.8125 14.3438V14.3457C11.8125 14.3724 11.8123 14.3991 11.8118 14.4257C10.4155 15.2664 8.77986 15.75 7.03125 15.75C5.28264 15.75 3.64698 15.2664 2.25069 14.4257Z"
                              stroke="#A88CA4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16.7986 3.70136C16.5302 3.43288 16.0949 3.43288 15.8264 3.70136L15.5233 4.00443L16.4956 4.9767L16.7986 4.67364C17.0671 4.40515 17.0671 3.96985 16.7986 3.70136Z"
                              fill="#A88CA4"
                            />
                            <path
                              d="M16.2178 5.25449L15.2455 4.28222L12.0631 7.46458C11.9016 7.62612 11.7829 7.82536 11.7176 8.04431L11.5082 8.74749C11.4876 8.81661 11.5065 8.89146 11.5575 8.94246C11.6085 8.99346 11.6834 9.01241 11.7525 8.99182L12.4557 8.78235C12.6746 8.71713 12.8739 8.59839 13.0354 8.43685L16.2178 5.25449Z"
                              fill="#A88CA4"
                            />
                          </svg>
                        </Box>
                        <Box className="metadata-block-content-wrapper">
                          <Typography className="metadata-block-content-title">Latest edit by</Typography>
                          <Typography className="metadata-block-content-text">{instance.updated_by}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </MetaDataItem>
                </Box>
              </MetaDataWrapper>
            )}

            {renderAffectedCpis()}

            {hasInstances && !instance && (
              <InstancesWrapper>
                <Box className="instances-inner">
                  <Typography className="instances-title">Instances ({integrationInstances.length})</Typography>

                  {integrationInstances.slice(0, 2).map((thisInstance: any, i: any) => {
                    return (
                      <IntegrationInstanceItem
                        key={i}
                        className="integration-instance-item"
                        sx={{
                          borderBottom:
                            integrationInstances.length === 1
                              ? ''
                              : integrationInstances.length === 2 && i === 0
                              ? `1px solid ${theme.colors.borderMedium}`
                              : integrationInstances.length === 2 && i === 1
                              ? ''
                              : `1px solid ${theme.colors.borderMedium}`,
                          paddingBottom:
                            (integrationInstances.length === 2 && i === 1) || integrationInstances.length === 1
                              ? '0'
                              : '16px'
                        }}
                      >
                        <Typography className="instances-instance-name">{thisInstance.name}</Typography>

                        <Box className="instance-blocks-list">
                          <Box className="instance-block-item">
                            <Box className="instance-block-icon-wrapper">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                              >
                                <path
                                  d="M11.8126 4.5C11.8126 6.0533 10.5534 7.3125 9.00013 7.3125C7.44682 7.3125 6.18763 6.0533 6.18763 4.5C6.18763 2.9467 7.44682 1.6875 9.00013 1.6875C10.5534 1.6875 11.8126 2.9467 11.8126 4.5Z"
                                  stroke="#A88CA4"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M3.37598 15.0887C3.42871 12.0277 5.92653 9.5625 9.00013 9.5625C12.0738 9.5625 14.5717 12.0278 14.6243 15.0889C12.9122 15.8745 11.0074 16.3125 9.00037 16.3125C6.99312 16.3125 5.0882 15.8744 3.37598 15.0887Z"
                                  stroke="#A88CA4"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M14.9399 5.2089C14.9609 5.08835 15.0702 5 15.1982 5H15.8018C15.9298 5 16.0391 5.08835 16.0601 5.2089L16.1098 5.49358C16.1243 5.5767 16.1825 5.64594 16.2597 5.68675C16.2769 5.69587 16.294 5.7053 16.3109 5.71502C16.3865 5.75863 16.4785 5.77219 16.5613 5.74259L16.8445 5.64128C16.9643 5.59841 17.0991 5.64456 17.1631 5.75039L17.4649 6.2496C17.5289 6.35544 17.5034 6.48994 17.4046 6.56761L17.1708 6.75135C17.1028 6.80486 17.069 6.88749 17.0706 6.97167C17.0708 6.98109 17.0709 6.99054 17.0709 7C17.0709 7.00946 17.0708 7.0189 17.0706 7.02832C17.069 7.1125 17.1028 7.19514 17.1708 7.24865L17.4046 7.43238C17.5034 7.51006 17.5289 7.64456 17.4649 7.75039L17.1631 8.2496C17.0991 8.35544 16.9643 8.40159 16.8445 8.35871L16.5613 8.25741C16.4785 8.22781 16.3865 8.24137 16.3109 8.28498C16.294 8.2947 16.277 8.30413 16.2597 8.31325C16.1825 8.35406 16.1243 8.4233 16.1098 8.50642L16.0601 8.7911C16.0391 8.91165 15.9298 9 15.8018 9H15.1982C15.0702 9 14.9609 8.91165 14.9399 8.7911L14.8902 8.50642C14.8757 8.4233 14.8175 8.35406 14.7403 8.31325C14.7231 8.30413 14.706 8.2947 14.6891 8.28498C14.6135 8.24137 14.5215 8.22781 14.4387 8.25741L14.1555 8.35872C14.0357 8.40159 13.9009 8.35544 13.8369 8.24961L13.5351 7.7504C13.4711 7.64456 13.4966 7.51006 13.5954 7.43239L13.8292 7.24865C13.8972 7.19514 13.931 7.11251 13.9294 7.02833C13.9292 7.01891 13.9291 7.00946 13.9291 7C13.9291 6.99054 13.9292 6.9811 13.9294 6.97168C13.931 6.8875 13.8972 6.80486 13.8292 6.75135L13.5954 6.56762C13.4966 6.48994 13.4711 6.35544 13.5351 6.24961L13.8369 5.7504C13.9009 5.64456 14.0357 5.59841 14.1555 5.64129L14.4387 5.74259C14.5215 5.77219 14.6135 5.75863 14.6891 5.71502C14.706 5.7053 14.723 5.69587 14.7403 5.68675C14.8175 5.64594 14.8757 5.5767 14.8902 5.49358L14.9399 5.2089Z"
                                  stroke="#A88CA4"
                                  strokeWidth="0.7"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M15.5 7H15.5075V7.0075H15.5V7Z"
                                  stroke="#A88CA4"
                                  strokeWidth="0.7"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </Box>
                            <Box className="instance-block-content-wrapper">
                              <Typography className="instance-block-content-title">Configured by</Typography>
                              <Typography className="instance-block-content-text">{thisInstance.created_by}</Typography>
                            </Box>
                          </Box>

                          <Box className="instance-block-item">
                            <Box className="instance-block-icon-wrapper">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="15"
                                height="14"
                                viewBox="0 0 15 14"
                                fill="none"
                              >
                                <path
                                  d="M3.06579 0.5V2.03947M10.25 0.5V2.03947M0.5 11.2763V3.57895C0.5 2.72872 1.18925 2.03947 2.03947 2.03947H11.2763C12.1265 2.03947 12.8158 2.72872 12.8158 3.57895V6.14474M0.5 11.2763C0.5 12.1265 1.18925 12.8158 2.03947 12.8158H7.3421M0.5 11.2763V6.14474C0.5 5.29451 1.18925 4.60526 2.03947 4.60526H11.2763C12.1265 4.60526 12.8158 5.29451 12.8158 6.14474M12.8158 6.14474V7.3421M14.2539 10.3366H13.0057M14.2539 10.3366V9.08846M14.2539 10.3366L13.4586 9.54089C13.211 9.29279 12.8967 9.10456 12.5338 9.00731C11.4335 8.7125 10.3026 9.36545 10.0077 10.4657M9.74609 12.9106V11.6624M9.74609 11.6624H10.9943M9.74609 11.6624L10.5413 12.4582C10.7889 12.7063 11.1032 12.8945 11.4662 12.9918C12.5664 13.2866 13.6974 12.6336 13.9922 11.5333"
                                  stroke="#A88CA4"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </Box>
                            <Box className="instance-block-content-wrapper">
                              <Typography className="instance-block-content-title">Date created</Typography>
                              <Typography className="instance-block-content-text">
                                {dayjs(thisInstance.created_at).format('MM/DD/YYYY')}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </IntegrationInstanceItem>
                    )
                  })}

                  {integrationInstances.length > 2 && (
                    <ThemeButton themevariant="hollow" onClick={() => handleViewInstances(integration)}>
                      View all
                    </ThemeButton>
                  )}
                </Box>
              </InstancesWrapper>
            )}
          </Box>

          {integrationType === 'add' ? (
            <ActionsWrapper className="actions-wrapper">
              {integrationInstances.length > 0 && (
                <ThemeButton
                  themevariant="secondary"
                  className="view-instances-button"
                  disabled={!hasInstances}
                  onClick={() => handleViewInstances(integration)}
                >
                  {`View Instance${integrationInstances.length === 1 ? '' : 's'}`}
                </ThemeButton>
              )}
              <ThemeButton
                themevariant="primary"
                className="add-instance-button"
                onClick={() => handleAddInstance(integration?.name || '')}
              >
                Add Instance
              </ThemeButton>
            </ActionsWrapper>
          ) : (
            <ActionsWrapper className="actions-wrapper">
              {noEditPermission ? (
                <PermissionTooltip
                  PopperProps={{
                    sx: {
                      marginTop: `-${theme.spacing.sm}px !important`
                    }
                  }}
                  title={
                    <>
                      <Box className="tooltip-wrapper-permissions one-line">
                        <Typography className="tooltip-text">Read Only</Typography>
                      </Box>
                    </>
                  }
                >
                  <ThemeButton
                    themevariant="secondary"
                    className="view-instances-button role-disabled"
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    sx={{
                      '&:hover': {
                        svg: {
                          path: {
                            fill: 'unset !important',
                            stroke: `${theme.colors.cta} !important`
                          }
                        }
                      }
                    }}
                  >
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                        <path
                          d="M10.3269 6L10.0962 12M6.90385 12L6.67308 6M13.3184 3.86038C13.5464 3.89481 13.7736 3.93165 14 3.97086M13.3184 3.86038L12.6065 13.115C12.5464 13.8965 11.8948 14.5 11.111 14.5H5.88905C5.10524 14.5 4.45358 13.8965 4.39346 13.115L3.68157 3.86038M13.3184 3.86038C12.5542 3.74496 11.781 3.65657 11 3.59622M3 3.97086C3.22638 3.93165 3.45358 3.89481 3.68157 3.86038M3.68157 3.86038C4.44585 3.74496 5.219 3.65657 6 3.59622M11 3.59622V2.98546C11 2.19922 10.3929 1.54282 9.60706 1.51768C9.23948 1.50592 8.87043 1.5 8.5 1.5C8.12957 1.5 7.76052 1.50592 7.39294 1.51768C6.6071 1.54282 6 2.19922 6 2.98546V3.59622M11 3.59622C10.175 3.53247 9.34131 3.5 8.5 3.5C7.65869 3.5 6.82496 3.53247 6 3.59622"
                          stroke={theme.colors.menuItemText}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Delete
                    </>
                  </ThemeButton>
                </PermissionTooltip>
              ) : (
                <ThemeButton
                  themevariant="secondary"
                  className="view-instances-button delete-button"
                  onClick={handleDeleteInstance}
                  sx={{
                    '&:hover': {
                      svg: {
                        path: {
                          fill: 'unset !important',
                          stroke: `${theme.colors.cta} !important`
                        }
                      }
                    },
                    '&:focus,&:active': {
                      svg: {
                        path: {
                          fill: 'unset !important',
                          stroke: `${theme.colors.cta} !important`
                        }
                      }
                    }
                  }}
                >
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                      <path
                        d="M10.3269 6L10.0962 12M6.90385 12L6.67308 6M13.3184 3.86038C13.5464 3.89481 13.7736 3.93165 14 3.97086M13.3184 3.86038L12.6065 13.115C12.5464 13.8965 11.8948 14.5 11.111 14.5H5.88905C5.10524 14.5 4.45358 13.8965 4.39346 13.115L3.68157 3.86038M13.3184 3.86038C12.5542 3.74496 11.781 3.65657 11 3.59622M3 3.97086C3.22638 3.93165 3.45358 3.89481 3.68157 3.86038M3.68157 3.86038C4.44585 3.74496 5.219 3.65657 6 3.59622M11 3.59622V2.98546C11 2.19922 10.3929 1.54282 9.60706 1.51768C9.23948 1.50592 8.87043 1.5 8.5 1.5C8.12957 1.5 7.76052 1.50592 7.39294 1.51768C6.6071 1.54282 6 2.19922 6 2.98546V3.59622M11 3.59622C10.175 3.53247 9.34131 3.5 8.5 3.5C7.65869 3.5 6.82496 3.53247 6 3.59622"
                        stroke={theme.colors.menuItemText}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Delete
                  </>
                </ThemeButton>
              )}

              {editBlockedReason ? (
                <PermissionTooltip
                  PopperProps={{
                    sx: {
                      marginTop: `-${theme.spacing.sm}px !important`
                    }
                  }}
                  title={
                    <>
                      <Box className="tooltip-wrapper-permissions one-line">
                        <Typography className="tooltip-text">{editBlockedReason}</Typography>
                      </Box>
                    </>
                  }
                >
                  <ThemeButton
                    themevariant="primary"
                    className="add-instance-button role-disabled"
                    disableFocusRipple
                    disableRipple
                    disableTouchRipple
                    sx={EDIT_BUTTON_SX}
                  >
                    {editButtonContent}
                  </ThemeButton>
                </PermissionTooltip>
              ) : (
                <ThemeButton
                  themevariant="primary"
                  className="add-instance-button"
                  onClick={() => instance && navigate(getInstanceEditUrl(instance))}
                  sx={EDIT_BUTTON_SX}
                >
                  {editButtonContent}
                </ThemeButton>
              )}
            </ActionsWrapper>
          )}
        </>
      </Box>
    </Drawer>
  )
}

export default IntegrationInstanceDrawer
